import { useTheme } from '../theme/ThemeContext';
import { useState, useEffect, useRef } from 'react';
import { fetchAverageRelativeAbundanceTaxaRank, fetchSumRelativeAbundanceTaxaRank } from './api';
// import AverageCountForOverlappingTaxon from '../utils/averageCountForOverlappingTaxon';
import AverageRelativeAbundanceForOverlappingTaxon from '../utils/averageRelativeAbundanceForOverlappingTaxon';
import EnrichmentScatterPlot from './widgets/enrichmentScatterPlot';
import Dropdown from './widgets/dropdown';
import Input from './widgets/input';
import { use } from 'react';
import { sum } from 'd3';

export default function SubSampleEnrichmentScatter({ multipleSamplesMetaData, multipleSamplesTaxaDataIndividual, isLoadingIndividualTaxa }) {
    // TODO: all thesee calculations should be done in the backend...
    // API should simply return the following:

    // taxon	count	log2fc
    // Neisseriaceae	3624	3.2782
    // Pasteurellaceae	3624	3.1506
    // Streptococcaceae	3624	2.5789
    // ...
    // Azonexaceae	2	-9.5231
    // Streptosporangiaceae	1	-11.6276
    // Heliobacteriaceae	1	-7.6631

    const { theme } = useTheme();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [rank, setRank] = useState("g");
    const [averageTaxa, setAverageTaxa] = useState(null);
    const [subSampleAverageTaxa, setSubSampleAverageTaxa] = useState(null);
    const [foldChangeData, setFoldChangeData] = useState(null);
    const [overlappingThreshold, setOverlappingThreshold] = useState(0);
    const [stepSize, setStepSize] = useState(1);
    const [sampleCount, setSampleCount] = useState(0);
    const abortControllerRef = useRef(null);


    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (multipleSamplesTaxaDataIndividual) {
            // setOverlappingThreshold(Math.ceil(multipleSamplesTaxaDataIndividual.length * 0.5));
            setOverlappingThreshold(0);
        }
    }, [multipleSamplesTaxaDataIndividual]);

    useEffect(() => {
        if (multipleSamplesTaxaDataIndividual) {

            setSampleCount(multipleSamplesTaxaDataIndividual.length);
            // transform the array of taxonomic data directly since we don't have run_assembly_ids
            const taxaData = multipleSamplesTaxaDataIndividual.reduce((acc, sample, index) => {
                // using array index as a substitute for run_assembly_id
                acc[`sample_${index}`] = sample.taxonomic_data;
                return acc;
            }, {});

            const avgRelativeAbundanceTaxa = AverageRelativeAbundanceForOverlappingTaxon({
                subSampleTaxaData: taxaData,
                rank,
                overlappingThreshold
            });

            setSubSampleAverageTaxa(avgRelativeAbundanceTaxa);
            setStepSize(Math.pow(10, String(multipleSamplesTaxaDataIndividual.length).length - 2));
            // setOverlappingThreshold(Math.ceil(multipleSamplesTaxaDataIndividual.length * 0.5));

        }
    }, [multipleSamplesTaxaDataIndividual, rank, overlappingThreshold]);



    useEffect(() => {
        const fetchSumTaxa = async () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            setIsLoading(true);
            setError(null);

            abortControllerRef.current = new AbortController();

            try {
                const sumRelativeAbundance = await fetchSumRelativeAbundanceTaxaRank(
                    rank,
                    abortControllerRef.current.signal
                );

                console.log("SumRelativeAbundance", sumRelativeAbundance);
                setAverageTaxa(sumRelativeAbundance);


            } catch (error) {
                if (error.name === 'AbortError') return;
                setError(error.message);
                console.error('Error fetching average taxa:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSumTaxa();
    }, [rank]);

    // calculate fold changes when both datasets are available
    useEffect(() => {
        if (subSampleAverageTaxa && averageTaxa) {
            const log2fc = subSampleAverageTaxa.map(item => {
                const avgItem = averageTaxa.taxa.find(avgItem => avgItem.name === item.name);
                if (avgItem) {
                    // global sum for this taxon
                    const globalSum = avgItem.sum_abundance;
                    // number of samples excluding subselection
                    const remainingSamples = averageTaxa.total_samples - item.sampleCount;

                    // subtract subselection sum from global sum
                    const remainingSum = globalSum - item.totalRelativeAbundance;
                    // average for remaining samples
                    const remainingAvg = remainingSum / remainingSamples;

                    // log2 fold change using corrected values
                    const log2fc = Math.log2(item.avgRelativeAbundance / remainingAvg);

                    return {
                        ...item,
                        log2fc,
                        totalAvgCount: remainingAvg,
                        sampleCount: item.sampleCount
                    };
                }
                return item;
            });

            const processedData = log2fc
                .filter(Boolean)
                .filter(d => isFinite(d.log2fc));

            setFoldChangeData(processedData);
        }
    }, [subSampleAverageTaxa, averageTaxa]);

    const handleRankSelection = (item) => {
        const rankDict = {
            'phylum': 'p',
            'class': 'c',
            'order': 'o',
            'family': 'f',
            'genus': 'g',
            'species': 's'
        };
        setRank(rankDict[item]);
    };


    return (
        <div>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            {multipleSamplesTaxaDataIndividual ? (
                (isLoading && isLoadingIndividualTaxa) ? (
                    <p>Loading fold change data...</p>
                ) : (
                    <>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            margin: '10px 0',
                            color: theme.colors.text,
                        }}>

                            <Dropdown
                                items={['phylum', 'class', 'order', 'family', 'genus', 'species']}
                                onSelect={handleRankSelection}
                                placeholder="genus"
                                fontSize='14px'
                            />
                        </div>
                        {foldChangeData ? (
                            <EnrichmentScatterPlot foldChangeData={foldChangeData} />
                            // <FoldChangeBarChart foldChangeData={foldChangeData} />
                        ) : (
                            <p>No fold change data available</p>
                        )}
                    </>
                )
            ) : (
                <p>Loading fold change data...</p>
            )}
        </div>
    );
}